import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Divider,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Grid
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    AccountBalance as AccountBalanceIcon,
    ExpandMore as ExpandMoreIcon,
    Person as PersonIcon,
    Visibility as VisibilityIcon,
    Receipt as ReceiptIcon,
    DirectionsCar as DirectionsCarIcon,
    Flight as FlightIcon,
    Train as TrainIcon,
    Close as CloseIcon,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { decryptText } from '../../../utils/encryption';
import accountsApiService from '../../../services/accountsApiService';
import { format } from 'date-fns';
import { generateSignedUrlForFilename } from '../../../utils/s3Utils';
import { toast } from 'react-toastify';

const AccountsDetails = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Get encrypted parameters from URL
    const encryptedIdFromURL = searchParams.get('id');
    const encryptedTypeFromURL = searchParams.get('type');

    const encryptedId = decryptText(encryptedIdFromURL);
    const encryptedType = decryptText(encryptedTypeFromURL);

    const [approvalData, setApprovalData] = useState({
        mainAssessment: [],
        preAssessment: [],
        qmsAssessment: [],
        reallotment: [],
        adminData: []
    });

    const [overallHonoranium, setOverallHonoranium] = useState(0);

    const [ceoRemarks, setCeoRemarks] = useState({
        main: '',
        pre: '',
        reallot: ''
    });

    const [loading, setLoading] = useState(false);
    const [assessmentType, setAssessmentType] = useState('');
    const [appId, setAppId] = useState('');
    const [actionStatus, setActionStatus] = useState({
        main: { processed: false, date: '', visible: false },
        pre: { processed: false, date: '', visible: false },
        reallot: { processed: false, date: '', visible: false },
        qms: { processed: false, date: '', visible: false }
    });

    // TADA Dialog state
    const [tadaDialogOpen, setTadaDialogOpen] = useState(false);
    const [selectedAssessor, setSelectedAssessor] = useState(null);
    const [tadaData, setTadaData] = useState([]);
    const [tadaLoading, setTadaLoading] = useState(false);
    const [assessorBasicDetails, setAssessorBasicDetails] = useState(null);
    const [basicDetailsLoading, setBasicDetailsLoading] = useState(false);

    useEffect(() => {
        if (encryptedId && encryptedType) {
            setAppId(encryptedId);
            setAssessmentType(encryptedType);
            fetchApprovalDetails();
        }
    }, [encryptedId, encryptedType]);

    const fetchApprovalDetails = async () => {
        try {
            setLoading(true);
            const response = await accountsApiService.getApprovalDetails({
                id: encryptedId,
                type: encryptedType
            });

            if (response.success && response.data) {
                setApprovalData(response.data);

                const overallHonoraniumfromApi = response.data.overallHonoranium.main
                setOverallHonoranium(overallHonoraniumfromApi);
                // Set CEO remarks and action status if available
                if (response.data.ceoRemarks) {
                    setCeoRemarks(response.data.ceoRemarks);
                }
                if (response.data.actionStatus) {
                    setActionStatus(response.data.actionStatus);
                }
            }
        } catch (error) {
            console.error('Error fetching approval details:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const handleViewReport = (item) => {
        // Open report in new tab
        window.open(`/preview-report?appId=${appId}&assmntId=${item.id}&assrId=${item.assessorId || item.allotedToId}`, '_blank');
    };

    // TADA related functions
    const fetchTadaData = async (assessorId, assessmentId) => {
        setTadaLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetAssessorTravel?assessmentid=${assessmentId}&assessorid=${assessorId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.isstatus && data.travel) {
                    setTadaData(data.travel);
                } else {
                    setTadaData([]);
                }
            } else {
                setTadaData([]);
            }
        } catch (error) {
            console.error('Error fetching TADA data:', error);
            setTadaData([]);
        } finally {
            setTadaLoading(false);
        }
    };

    const fetchAssessorBasicDetails = async (assessorId, assessmentId) => {
        setBasicDetailsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/assessor/GetAssessorBasicDetails?assessorid=${assessorId}&assessmentid=${assessmentId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setAssessorBasicDetails(data.data);
                } else {
                    setAssessorBasicDetails(null);
                }
            } else {
                setAssessorBasicDetails(null);
            }
        } catch (error) {
            console.error('Error fetching assessor basic details:', error);
            setAssessorBasicDetails(null);
        } finally {
            setBasicDetailsLoading(false);
        }
    };

    const handleViewTada = async (assessor) => {
        setSelectedAssessor(assessor);
        setTadaDialogOpen(true);
        
        // Fetch both TADA data and basic details in parallel
        const assessorId = assessor.allotedToId[0] || assessor.allotedToId;
        const assessmentId = assessor.AssessorAllotmentId;
        
        await Promise.all([
            fetchTadaData(assessorId, assessmentId),
            fetchAssessorBasicDetails(assessorId, assessmentId)
        ]);
    };

    const handleCloseTadaDialog = () => {
        setTadaDialogOpen(false);
        setSelectedAssessor(null);
        setTadaData([]);
        setAssessorBasicDetails(null);
    };

    const handleViewTravelBill = async (billFileName) => {
        if (!billFileName) {
            toast.error('No bill file available to view');
            return;
        }

        try {
            // Generate signed URL for the travel bill using S3
            const signedUrl = await generateSignedUrlForFilename(billFileName);

            if (signedUrl) {
                // Open the signed URL in a new tab
                window.open(signedUrl, '_blank');
            } else {
                toast.error('Unable to generate file access URL');
            }
        } catch (error) {
            console.error('Error viewing travel bill:', error);
            toast.error('Error accessing bill file: ' + error.message);
        }
    };

    const exportToExcel = async () => {
        try {
            // Dynamic import of xlsx
            const XLSX = await import('xlsx');
            
            // Create a new workbook
            const workbook = XLSX.utils.book_new();
            
            // Prepare data for different assessment types
            const allData = [];
            
            // Add Main Assessment data
            if (approvalData.mainAssessment && approvalData.mainAssessment.length > 0) {
                approvalData.mainAssessment.forEach(item => {
                    allData.push({
                        'Assessment Type': 'Main Assessment',
                        'Application ID': item.applicationId || appId,
                        'Assessor Name': item.assessorName || 'N/A',
                        'Assessor Code': item.assessorCode || 'N/A',
                        'Assessment Date': item.assessmentDate || 'N/A',
                        'Days': item.days || 0,
                        'Honorarium': item.honoranium || 0,
                        'Travel Allowance': item.travelAllowance || 0,
                        'Daily Allowance': item.dailyAllowance || 0,
                        'Total Amount': item.totalAmount || 0,
                        'Status': item.status || 'N/A',
                        'Remarks': item.remarks || 'N/A'
                    });
                });
            }
            
            // Add Pre Assessment data
            if (approvalData.preAssessment && approvalData.preAssessment.length > 0) {
                approvalData.preAssessment.forEach(item => {
                    allData.push({
                        'Assessment Type': 'Pre Assessment',
                        'Application ID': item.applicationId || appId,
                        'Assessor Name': item.assessorName || 'N/A',
                        'Assessor Code': item.assessorCode || 'N/A',
                        'Assessment Date': item.assessmentDate || 'N/A',
                        'Days': item.days || 0,
                        'Honorarium': item.honoranium || 0,
                        'Travel Allowance': item.travelAllowance || 0,
                        'Daily Allowance': item.dailyAllowance || 0,
                        'Total Amount': item.totalAmount || 0,
                        'Status': item.status || 'N/A',
                        'Remarks': item.remarks || 'N/A'
                    });
                });
            }
            
            // Add QMS Assessment data
            if (approvalData.qmsAssessment && approvalData.qmsAssessment.length > 0) {
                approvalData.qmsAssessment.forEach(item => {
                    allData.push({
                        'Assessment Type': 'QMS Assessment',
                        'Application ID': item.applicationId || appId,
                        'Assessor Name': item.assessorName || 'N/A',
                        'Assessor Code': item.assessorCode || 'N/A',
                        'Assessment Date': item.assessmentDate || 'N/A',
                        'Days': item.days || 0,
                        'Honorarium': item.honoranium || 0,
                        'Travel Allowance': item.travelAllowance || 0,
                        'Daily Allowance': item.dailyAllowance || 0,
                        'Total Amount': item.totalAmount || 0,
                        'Status': item.status || 'N/A',
                        'Remarks': item.remarks || 'N/A'
                    });
                });
            }
            
            // Add Reallotment data
            if (approvalData.reallotment && approvalData.reallotment.length > 0) {
                approvalData.reallotment.forEach(item => {
                    allData.push({
                        'Assessment Type': 'Reallotment',
                        'Application ID': item.applicationId || appId,
                        'Assessor Name': item.assessorName || 'N/A',
                        'Assessor Code': item.assessorCode || 'N/A',
                        'Assessment Date': item.assessmentDate || 'N/A',
                        'Days': item.days || 0,
                        'Honorarium': item.honoranium || 0,
                        'Travel Allowance': item.travelAllowance || 0,
                        'Daily Allowance': item.dailyAllowance || 0,
                        'Total Amount': item.totalAmount || 0,
                        'Status': item.status || 'N/A',
                        'Remarks': item.remarks || 'N/A'
                    });
                });
            }
            
            // Create worksheet from data
            if (allData.length > 0) {
                const worksheet = XLSX.utils.json_to_sheet(allData);
                
                // Add the worksheet to workbook
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Assessment Details');
                
                // Generate filename with current date
                const currentDate = new Date().toISOString().split('T')[0];
                const filename = `Assessor_Travel_Details_${appId}_${currentDate}.xlsx`;
                
                // Save the file
                XLSX.writeFile(workbook, filename);
                
                toast.success(`Excel file "${filename}" downloaded successfully!`);
            } else {
                toast.warning('No data available to export');
            }
            
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Error exporting data to Excel. Please make sure xlsx library is installed.');
        }
    };

    // Helper functions for TADA dialog
    const getModeIcon = (mode) => {
        switch (mode?.toLowerCase()) {
            case 'train':
            case 'railway':
                return <TrainIcon fontSize="small" />;
            case 'flight':
            case 'air':
            case 'aeroplane':
                return <FlightIcon fontSize="small" />;
            case 'road':
            case 'car':
            case 'taxi':
            case 'bus':
                return <DirectionsCarIcon fontSize="small" />;
            default:
                return <DirectionsCarIcon fontSize="small" />;
        }
    };

    const getModeColor = (mode) => {
        switch (mode?.toLowerCase()) {
            case 'train':
            case 'railway':
                return 'primary';
            case 'flight':
            case 'air':
            case 'aeroplane':
                return 'error';
            case 'road':
            case 'car':
            case 'taxi':
                return 'warning';
            case 'bus':
                return 'success';
            default:
                return 'default';
        }
    };

    const renderAssessmentTable = (data, section, title, honoranium) => {
        if (!data || data.length === 0) return null;

        return (
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        {title}
                    </Typography>
                    <Paper elevation={2}>
                        <Box sx={{ p: 2, bgcolor: 'primary.50' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                Overall Honorarium Amount: ₹{Number(overallHonoranium).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 50 }}>S. No</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 250 }}>Laboratory & Assessment Info</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Assessor Details</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Assessment Period</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Amounts</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Final Amount & Remarks</TableCell>
                                        <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Requested to Accounts on</TableCell>
                                        {/* <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Documents</TableCell> */}
                                        <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((item, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{index + 1}</TableCell>
                                            {/* Laboratory & Assessment Info */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} noWrap>
                                                        {item.laboratoryName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        Application No: {item.applicationNo}
                                                    </Typography>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <Typography variant="body2" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        {item.disciplineName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        Group: {item.groups || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {/* Assessor Details */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {item.assessorName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        ID: {item.loginName}
                                                    </Typography>
                                                    <br />
                                                    <Chip
                                                        label={item.capacity}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                        sx={{ mt: 0.5 }}
                                                    />
                                                    <Typography variant="caption" display="block" color="text.primary">
                                                        Contact: {item.contact}
                                                    </Typography>
                                                    <Typography variant="caption" display="block" color="text.primary" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        Organisation: {item.currentEmployer}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {/* Assessment Period */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2">
                                                        <strong>From:</strong> {item.dateFrom}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>To:</strong> {item.dateTo}
                                                    </Typography>
                                                    {/* <Box sx={{ mt: 1 }}>
                                                                           {item.visFile === 0 ? (
                                                                               <Chip label="Report N/A" size="small" color="error" variant="outlined" />
                                                                           ) : (
                                                                               <IconButton 
                                                                                   size="small" 
                                                                                   color="primary"
                                                                                   onClick={() => handleViewReport(item)}
                                                                               >
                                                                                   <VisibilityIcon />
                                                                               </IconButton>
                                                                           )}
                                                                       </Box> */}
                                                </Box>
                                            </TableCell>
                                            {/* Amounts */}
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" color="success.main" fontWeight={600}>
                                                        Hon: ₹{Number(item.honAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                    </Typography>
                                                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                                                        Amount: ₹{Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {/* Final Amount & Remarks */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
                                                            Final Amount:
                                                        </Typography>
                                                        <Typography variant="body2" color="success.main" fontWeight={600}>
                                                            ₹{Number(item.finalAmount || item.totalAmount || '0').toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
                                                            Remarks:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                                                            {item.remarks || 'No remarks'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(item.RequestDate)}
                                                </Typography>
                                            </TableCell>
                                            {/* Documents */}
                                            {/* <TableCell>
                                                                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                       <Box>
                                                                           <Typography variant="caption" fontWeight={600}>PAN:</Typography>
                                                                           <Typography variant="caption" display="block">
                                                                               {item.panUploadFile || 'N/A'}
                                                                           </Typography>
                                                                       </Box>
                                                                       <Box>
                                                                           <Typography variant="caption" fontWeight={600}>Cheque:</Typography>
                                                                           {item.cancelChequeUploadfile ? (
                                                                               <Button 
                                                                                   size="small" 
                                                                                   variant="text"
                                                                                   href={item.cancelChequeUploadfile} 
                                                                                   target="_blank"
                                                                                   sx={{ p: 0, minWidth: 'auto', fontSize: '0.7rem' }}
                                                                               >
                                                                                   View
                                                                               </Button>
                                                                           ) : (
                                                                               <Typography variant="caption" color="text.primary">N/A</Typography>
                                                                           )}
                                                                       </Box>
                                                                       <Box>
                                                                           <Typography variant="caption" fontWeight={600}>New PAN:</Typography>
                                                                           {item.newPanUploadFile ? (
                                                                               <Button 
                                                                                   size="small" 
                                                                                   variant="text"
                                                                                   href={item.newPanUploadFile} 
                                                                                   target="_blank"
                                                                                   sx={{ p: 0, minWidth: 'auto', fontSize: '0.7rem' }}
                                                                               >
                                                                                   View
                                                                               </Button>
                                                                           ) : (
                                                                               <Typography variant="caption" color="text.primary">N/A</Typography>
                                                                           )}
                                                                       </Box>
                                                                       <Box>
                                                                           <Typography variant="caption" fontWeight={600}>New Cheque:</Typography>
                                                                           {item.newCancelChequeUploadfile ? (
                                                                               <Button 
                                                                                   size="small" 
                                                                                   variant="text"
                                                                                   href={item.newCancelChequeUploadfile} 
                                                                                   target="_blank"
                                                                                   sx={{ p: 0, minWidth: 'auto', fontSize: '0.7rem' }}
                                                                               >
                                                                                   View
                                                                               </Button>
                                                                           ) : (
                                                                               <Typography variant="caption" color="text.primary">N/A</Typography>
                                                                           )}
                                                                       </Box>
                                                                   </Box>
                                                               </TableCell> */}

                                            {/* Actions */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        onClick={() => handleViewTada(item)}
                                                        startIcon={<ReceiptIcon />}
                                                        sx={{ minWidth: 70, fontSize: '0.75rem' }}
                                                    >
                                                        View TADA
                                                    </Button>
                                                </Box>
                                            </TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading accounts details...
                </Typography>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
            {/* Header */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AccountBalanceIcon color="primary" sx={{ fontSize: 32 }} />
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Assessor Travel Details
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button 
                            variant="contained"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={exportToExcel}
                            sx={{ minWidth: 160 }}
                        >
                            Export to Excel
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ minWidth: 120 }}
                        >
                            Go Back
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Content */}
            <Box sx={{ minHeight: 400 }}>
                {/* Main Assessment */}
                {assessmentType === 'Main' && approvalData.mainAssessment.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {renderAssessmentTable(
                            approvalData.mainAssessment,
                            'main',
                            'Alloted Assessors For Assessment',
                            overallHonoranium
                        )}

                        {/* CEO Remarks */}
                        {ceoRemarks.main && (
                            <Card sx={{ mb: 2, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        CEO Remarks:
                                    </Typography>
                                    <Typography variant="body1" color="text.primary">
                                        {ceoRemarks.main}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}

                        {/* Status */}
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            {actionStatus.main.processed ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Action Performed Successfully on {actionStatus.main.date}
                                    </Typography>
                                </Alert>
                            ) : approvalData.mainAssessment.every(item => item.Is_Processed === 1) ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        All entries have been processed on {approvalData.mainAssessment[0].Processed_Date ? format(new Date(approvalData.mainAssessment[0].Processed_Date), 'dd MMM yyyy') : ''}
                                    </Typography>
                                </Alert>
                            ) : (
                                <Alert severity="info" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body2">
                                        Processing in progress...
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Reallotment */}
                {assessmentType === 'Main' && approvalData.reallotment.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {renderAssessmentTable(
                            approvalData.reallotment,
                            'reallot',
                            'Realloted Assessors For Assessment',
                            overallHonoranium
                        )}

                        {/* CEO Remarks */}
                        {ceoRemarks.reallot && (
                            <Card sx={{ mb: 2, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        CEO Remarks:
                                    </Typography>
                                    <Typography variant="body1" color="text.primary">
                                        {ceoRemarks.reallot}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}

                        {/* Status */}
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                            {actionStatus.reallot.processed ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Action Performed Successfully on {actionStatus.reallot.date}
                                    </Typography>
                                </Alert>
                            ) : approvalData.reallotment.every(item => item.Is_Processed === 1) ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        All entries have been processed
                                    </Typography>
                                </Alert>
                            ) : (
                                <Alert severity="info" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body2">
                                        Processing in progress...
                                    </Typography>
                                </Alert>
                            )}
                        </Card>
                    </Box>
                )}

                {/* Pre Assessment */}
                {assessmentType === 'Pre' && approvalData.preAssessment.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {renderAssessmentTable(
                            approvalData.preAssessment,
                            'pre',
                            'Alloted Assessors For Pre-Assessment',
                            overallHonoranium
                        )}

                        {/* CEO Remarks */}
                        {ceoRemarks.pre && (
                            <Card sx={{ mb: 2, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        CEO Remarks:
                                    </Typography>
                                    <Typography variant="body1" color="text.primary">
                                        {ceoRemarks.pre}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}

                        {/* Status */}
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                            {actionStatus.pre.processed ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Action Performed Successfully on {actionStatus.pre.date}
                                    </Typography>
                                </Alert>
                            ) : approvalData.preAssessment.every(item => item.Is_Processed === 1) ? (
                                <Alert severity="success" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        All entries have been processed
                                    </Typography>
                                </Alert>
                            ) : (
                                <Alert severity="info" sx={{ justifyContent: 'center' }}>
                                    <Typography variant="body2">
                                        Processing in progress...
                                    </Typography>
                                </Alert>
                            )}
                        </Card>
                    </Box>
                )}

                {/* Admin Data */}
                {approvalData.adminData.length > 0 && (
                    <Accordion sx={{ mb: 3 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon color="info" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Admin Proposed Details
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Paper elevation={2}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Laboratory Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Assessor</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Capacity</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>From Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>To Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Dealing Office Travel Plan</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Admin Proposed Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Admin Travel Plan Detail</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {approvalData.adminData.map((item, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{item.laboratoryName}</TableCell>
                                                    <TableCell>
                                                        <Chip label={item.assessmentType} size="small" color="primary" />
                                                    </TableCell>
                                                    <TableCell>{item.assessorName}</TableCell>
                                                    <TableCell>
                                                        <Chip label={item.capacity} size="small" color="secondary" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>{item.dateFrom}</TableCell>
                                                    <TableCell>{item.dateTo}</TableCell>
                                                    <TableCell>
                                                        <Stack spacing={1}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                href={item.files}
                                                                target="_blank"
                                                                startIcon={<VisibilityIcon />}
                                                            >
                                                                View File
                                                            </Button>
                                                            <Typography variant="caption" color="text.primary">
                                                                <strong>Remarks:</strong> {item.remark}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="success.main" fontWeight={600}>
                                                            ₹₹{Number(item.amount).toLocaleString("en-IN")}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack spacing={1}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                href={item.adminUpload}
                                                                target="_blank"
                                                                startIcon={<VisibilityIcon />}
                                                            >
                                                                View File
                                                            </Button>
                                                            <Typography variant="caption" color="text.primary">
                                                                <strong>Remarks:</strong> {item.adminRemarks}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* No Data Message */}
                {!loading &&
                    assessmentType &&
                    approvalData.mainAssessment.length === 0 &&
                    approvalData.preAssessment.length === 0 &&
                    approvalData.qmsAssessment.length === 0 && (
                        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                            <Alert severity="info">
                                <Typography variant="h6">No accounts data available</Typography>
                                <Typography variant="body2" color="text.primary">
                                    No assessment data found for this application and assessment type.
                                </Typography>
                            </Alert>
                        </Paper>
                    )}

                {/* TADA Details Dialog */}
                <Dialog
                    open={tadaDialogOpen}
                    onClose={handleCloseTadaDialog}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: { maxHeight: '90vh', overflow: 'hidden' }
                    }}
                >
                    <DialogTitle sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                TADA Details - {selectedAssessor?.assessorName || 'N/A'}
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseTadaDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ p: 0 }}>
                        {(tadaLoading || basicDetailsLoading) ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ p: 3 }}>
                                {/* Assessor Basic Details Section */}
                                {assessorBasicDetails && (
                                    <Card sx={{ mb: 3 }}>
                                        <CardHeader
                                            avatar={<PersonIcon color="primary" />}
                                            title="Assessor Basic Details"
                                            sx={{ pb: 1 }}
                                        />
                                        <CardContent sx={{ pt: 0 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Name
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Name || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Email
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Officeemail || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Mobile
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.OfficeMobile || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            PAN Number
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.PANCard || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Address
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.address || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Bank Details
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Bank_Details || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Account Number
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Account_No || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            IFSC Code
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Ifcs_Code || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Bank Address
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Bank_Address || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                {assessorBasicDetails.Car_RegNo && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                                Car Registration No
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {assessorBasicDetails.Car_RegNo}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                                <Grid item xs={12}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Laboratory Name
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.LaboratoryName || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                            Purpose & Assessment Period
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {assessorBasicDetails.Purpose || 'N/A'} 
                                                            {assessorBasicDetails.DateFrom && assessorBasicDetails.DateTo && 
                                                                ` & ${assessorBasicDetails.DateFrom} To ${assessorBasicDetails.DateTo}`
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Travel Claims Section */}
                                {tadaData.length > 0 ? (
                                    <Card>
                                        <CardHeader
                                            avatar={<DirectionsCarIcon color="success" />}
                                            title="Travel Claims"
                                            sx={{ pb: 1 }}
                                        />
                                        <CardContent sx={{ pt: 0 }}>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                            <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Bills</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tadaData.map((claim, index) => (
                                                        <TableRow key={claim.id || index} hover>
                                                            <TableCell>
                                                                <Chip
                                                                    icon={getModeIcon(claim.modeOfTravel)}
                                                                    label={claim.modeOfTravel}
                                                                    size="small"
                                                                    color={getModeColor(claim.modeOfTravel)}
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        {claim.dep_From} → {claim.arrival_To}
                                                                    </Typography>
                                                                    {/* {claim.distance && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Distance: {claim.distance} km
                                                                        </Typography>
                                                                    )} */}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        Departure: {claim.dep_Date}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {claim.dep_Time}
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                                                                        Arrival: {claim.arrival_Date}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {claim.arrival_Time}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={600} color="success.main">
                                                                    ₹{claim.amount}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={claim.is_Origional_Bill === "1" ? 'Available' : 'Not Available'}
                                                                    size="small"
                                                                    color={claim.is_Origional_Bill === "1" ? 'success' : 'error'}
                                                                    variant="filled"
                                                                />
                                                                {claim.bill && (
                                                                    <Box sx={{ mt: 0.5 }}>
                                                                        <Button
                                                                            startIcon={<VisibilityIcon />}
                                                                            size="small"
                                                                            variant="text"
                                                                            onClick={() => handleViewTravelBill(claim.bill)}
                                                                            sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                                                                        >
                                                                            View Bill
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label="Submitted"
                                                                    size="small"
                                                                    color="success"
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {/* Total Amount */}
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'right' }}>
                                            <Typography variant="h6" fontWeight={600} color="#fff">
                                                Total Claim Amount: ₹{tadaData.reduce((sum, claim) => sum + parseInt(claim.amount || 0), 0)}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Card>
                                    <CardContent>
                                        <Box sx={{ textAlign: 'center', p: 4 }}>
                                            <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                                No TADA Details Available
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                No travel claims have been submitted for this assessor.
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Button onClick={handleCloseTadaDialog} variant="outlined">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default AccountsDetails;